import { useTranslation } from 'react-i18next'
import { Alert } from '@/components/ui/Alert'
import { useChildDashboard } from '../hooks/useParental'

interface ChildDashboardPanelProps {
  readonly childId: string
}

/**
 * Ce que le parent voit de l'activité de son enfant (§ 8.2) : des durées, une
 * progression, des objectifs. Jamais le contenu des conversations, que les
 * spécifications protègent explicitement.
 */
export function ChildDashboardPanel({ childId }: ChildDashboardPanelProps) {
  const { t, i18n } = useTranslation()
  const { data, isLoading, isError } = useChildDashboard(childId)

  if (isError) {
    return <Alert variant="error">{t('parent.dashboard.load_error')}</Alert>
  }

  if (isLoading || !data) {
    return <div className="h-40 animate-pulse rounded-2xl bg-surface-container" />
  }

  const formatDate = (iso: string | null) =>
    iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(iso)) : '—'

  const metrics = [
    ['mastery', t('parent.dashboard.percent', { value: data.mastery_percent })],
    ['streak', t('parent.dashboard.days', { count: data.streak_days })],
    ['week', t('parent.dashboard.minutes', { count: data.minutes_this_week })],
    ['month', t('parent.dashboard.minutes', { count: data.minutes_this_month })],
  ] as const

  return (
    <section aria-labelledby="child-dashboard-title" className="flex flex-col gap-4">
      <h2 id="child-dashboard-title" className="font-display text-lg font-semibold text-on-surface">
        {t('parent.dashboard.title')}
      </h2>

      <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {metrics.map(([key, value]) => (
          <div key={key} className="rounded-2xl bg-surface-low px-4 py-3">
            <dt className="text-xs font-medium text-on-surface-variant">
              {t(`parent.dashboard.metrics.${key}`)}
            </dt>
            <dd className="font-display text-xl font-bold text-on-surface">{value}</dd>
          </div>
        ))}
      </dl>

      <div className="rounded-2xl bg-surface-low px-4 py-3">
        <p className="text-xs font-medium text-on-surface-variant">
          {t('parent.dashboard.last_session')}
        </p>
        {data.last_session ? (
          <p className="mt-1 text-sm text-on-surface">
            {t('parent.dashboard.last_session_detail', {
              course: data.last_session.course_title ?? '—',
              minutes: data.last_session.duration_minutes,
              date: formatDate(data.last_session.ended_at),
            })}
          </p>
        ) : (
          <p className="mt-1 text-sm text-on-surface-variant">{t('parent.dashboard.no_session')}</p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold text-on-surface">
          {t('parent.dashboard.objectives')}
        </h3>
        {data.objectives.length === 0 ? (
          <p className="text-sm text-on-surface-variant">{t('parent.dashboard.no_objective')}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {data.objectives.map((objective) => (
              <li
                key={objective.id}
                className="flex items-center justify-between gap-3 rounded-2xl bg-surface-low px-4 py-2 text-sm"
              >
                <span className="truncate text-on-surface">{objective.title}</span>
                <span className="shrink-0 text-xs text-on-surface-variant">
                  {formatDate(objective.target_date)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {data.recent_badges.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-on-surface">{t('parent.dashboard.badges')}</h3>
          <ul className="flex flex-wrap gap-2">
            {data.recent_badges.map((badge) => (
              <li
                key={badge.code}
                className="rounded-full bg-secondary/10 px-3 py-1 text-xs font-semibold text-secondary"
              >
                {t(`gamification.badges.${badge.code}.name`)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}
